import React, { useEffect, useState } from "react";
import { authFetch } from "../utils/authFetch";
import { Bell } from "lucide-react";
import { useTranslation } from 'react-i18next';

const AllNotifications = () => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [hasUnread, setHasUnread] = useState(false);

  useEffect(() => {
    const fetchAllNotifications = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        console.warn("[WARN] No token found, skipping notification fetch.");
        return;
      }

      try {
        const res = await authFetch.get("/notifications/all");
        setNotifications(res.data || []);
        console.log("[DEBUG] Loaded notifications:", res.data);
        setHasUnread(res.data.some((n) => !n.read));
      } catch (err) {
        console.error("Failed to fetch all notifications:", err.response?.data || err.message);
      }
    };

    if (localStorage.getItem('forceReloadNotifications') === '1') {
      fetchAllNotifications();
      localStorage.removeItem('forceReloadNotifications');
    } else {
      fetchAllNotifications();
    }
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString("bg-BG", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleMarkAsRead = async (id) => {
    try {
      await authFetch.patch(`/notifications/${id}/read`);
      const updated = notifications.map(n =>
        n._id === id ? { ...n, read: true } : n
      );
      setNotifications(updated);
      setHasUnread(updated.some(n => !n.read));
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center pt-16">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('allNotifications.notifications')}</h1>
          <p className="text-gray-600 mt-2">{t('allNotifications.viewAndManageNotifications')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Bell className={`w-6 h-6 ${hasUnread ? "text-red-600" : "text-gray-400"}`} />
          {hasUnread && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              {t('allNotifications.unread')}
            </span>
          )}
        </div>
      </div>

      {/* Notifications Summary */}
      <div className="text-sm text-gray-600 font-semibold">
        {t('allNotifications.totalNotifications')}: {notifications.length}
        {hasUnread && (
          <span className="ml-4">
            {t('allNotifications.unread')}: {notifications.filter(n => !n.read).length}
          </span>
        )}
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
        {notifications.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('allNotifications.noNotifications')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('allNotifications.allCaughtUp')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('allNotifications.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('allNotifications.message')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('allNotifications.date')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('allNotifications.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {notifications.map((n) => (
                  <tr key={n._id} className={`hover:bg-gray-50 ${!n.read ? 'bg-red-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {!n.read ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {t('allNotifications.unread')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {t('allNotifications.read')}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className={`${!n.read ? 'font-semibold' : ''}`}>
                        {n.message}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(n.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {!n.read && (
                        <button
                          onClick={() => handleMarkAsRead(n._id)}
                          className="text-blue-600 hover:text-blue-900"
                          title={t('allNotifications.markAsRead')}
                        >
                          {t('allNotifications.markAsRead')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>


    </div>
  );
};

export default AllNotifications;
